// ============================================================================
// AUTH SERVICE - Cognito OAuth 2.0 Integration
// Handles authentication flow with AWS Cognito
// ============================================================================

import { Amplify } from 'aws-amplify';
import { signInWithRedirect, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

// Configure Amplify with Cognito settings
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
      userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
      loginWith: {
        oauth: {
          domain: process.env.REACT_APP_COGNITO_DOMAIN || '',
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: [process.env.REACT_APP_COGNITO_REDIRECT_URI || 'http://localhost:3000/auth/callback'],
          redirectSignOut: [process.env.REACT_APP_COGNITO_LOGOUT_URI || 'http://localhost:3000/'],
          responseType: 'code'
        }
      }
    }
  }
});

export interface UserProfile {
  userId: string;
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  organizationId: string;
  navPreferences?: { pinnedItems: string[] };
  // Global Cedar actions the user holds, from /me. Display only: the backend checks every call.
  permissions: string[];
  roles: Array<{ id: string; name: string }>;
  breakGlassEligible: boolean;
}

/**
 * Sign in with Microsoft (Entra ID).
 *
 * The provider must be named explicitly. Calling signInWithRedirect() with no
 * argument resolves to identity_provider=COGNITO, which serves the password
 * page — a dead end for anyone who only exists as a federated identity, since
 * they have no password to give.
 */
export const loginWithEntra = async (): Promise<void> => {
  try {
    await signInWithRedirect({ provider: { custom: 'EntraID' } });
  } catch (error) {
    console.error('Microsoft login error:', error);
    throw error;
  }
};

/**
 * Initiate OAuth 2.0 login flow
 * Redirects to Cognito Hosted UI
 */
export const login = async (): Promise<void> => {
  try {
    await signInWithRedirect();
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Logout user and clear session
 */
export const logout = async (): Promise<void> => {
  try {
    await signOut();
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

/**
 * Get current authenticated user
 */
export const getCurrentAuthenticatedUser = async (): Promise<any> => {
  try {
    const user = await getCurrentUser();
    return user;
  } catch (error) {
    console.error('Get current user error:', error);
    return null;
  }
};

/**
 * Get current session with tokens
 */
export const getSession = async (): Promise<any> => {
  try {
    const session = await fetchAuthSession();
    return session;
  } catch (error) {
    console.error('Get session error:', error);
    return null;
  }
};

/**
 * Get ID token (JWT) for API requests
 */
export const getIdToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error('Get ID token error:', error);
    return null;
  }
};

export class AccountPendingApprovalError extends Error {
  constructor(message?: string) {
    super(message || 'Your access request is awaiting approval');
    this.name = 'AccountPendingApprovalError';
  }
}

/**
 * Fetch user profile from backend
 */
export const fetchUserProfile = async (idToken: string): Promise<UserProfile | null> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/users/me`, {
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      // Self-provisioned but not yet approved. Distinguished from a failure so
      // the callback screen can explain the wait instead of showing a generic
      // login error, which reads as the platform being broken.
      if (response.status === 403) {
        const body = await response.json().catch(() => ({}));
        if (body.code === 'ACCOUNT_PENDING_APPROVAL') {
          throw new AccountPendingApprovalError(body.error);
        }
      }
      throw new Error('Failed to fetch user profile');
    }

    const data = await response.json();
    return data.user;
  } catch (error) {
    if (error instanceof AccountPendingApprovalError) throw error;
    console.error('Fetch user profile error:', error);
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    const session = await getSession();
    return !!session?.tokens?.idToken;
  } catch (error) {
    return false;
  }
};
